/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { T } from "./Translate";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Badge as BadgeIcon,
  Close as CloseIcon,
} from "../icons";
import { brandColors } from "../theme";
import {
  parsePhoneNumber,
  AsYouType,
  getCountries,
  getCountryCallingCode,
} from "libphonenumber-js";
import { auth, db, createUser, completeGoogleProfile } from "../firebase";
import { searchAddressSuggestions } from "./eventsUtils";
import AuthLayout from "./AuthLayout";
import logo13 from "../logo13.png";

const logo15 = `${process.env.PUBLIC_URL}/logo15.png`;

// Países database using libphonenumber-js
const countriesData = getCountries()
  .map((countryCode) => {
    try {
      return {
        code: countryCode,
        name: new Intl.DisplayNames(["es"], { type: "region" }).of(countryCode),
        phoneCode: `+${getCountryCallingCode(countryCode)}`,
        flag: countryCode
          .toUpperCase()
          .replace(/./g, (char) =>
            String.fromCodePoint(char.charCodeAt(0) + 127397)
          ),
      };
    } catch (e) {
      return null;
    }
  })
  .filter(Boolean)
  .sort((a, b) => a.name.localeCompare(b.name));

// Terms and Conditions Component
const TermsDialog = ({ open, onClose, onAccept }) => {
  const [scrolled, setScrolled] = useState(false);

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 50;
    if (bottom) {
      setScrolled(true);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          height: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6" component="span">
          <T>Términos y Condiciones</T>
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers onScroll={handleScroll} sx={{ px: 3 }}>
        <Box sx={{ textAlign: "left" }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            <T>Bienvenido a Notiline</T>
          </Typography>

          <Typography variant="body1" paragraph>
            <T>Estos términos y condiciones describen las reglas y regulaciones para el uso de Notiline.</T>
          </Typography>

          <Typography variant="body1" paragraph>
            <T>Al usar esta aplicación, asumimos que aceptas estos términos y condiciones. No continúes usando Notiline si no estás de acuerdo con todos los términos y condiciones establecidos en esta página.</T>
          </Typography>

          <Typography variant="body1" paragraph>
            <T>La siguiente terminología se aplica a estos Términos y Condiciones, Declaración de Privacidad y Aviso de Descargo de Responsabilidad: "Cliente", "Tú" y "Tu" se refiere a ti, la persona que accede a este sitio web y acepta los términos y condiciones de la Compañía. "La Compañía", "Nosotros" y "Nuestro", se refiere a nuestra Compañía.</T>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            <T>Licencia</T>
          </Typography>

          <Typography variant="body1" paragraph>
            <T>A menos que se indique lo contrario, Notiline y/o sus licenciantes poseen los derechos de propiedad intelectual de todo el material en Notiline. Todos los derechos de propiedad intelectual están reservados.</T>
          </Typography>

          <Typography variant="body1" paragraph>
            <T>No debes:</T>
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body2"><T>Republicar material de Notiline</T></Typography>
            <Typography component="li" variant="body2"><T>Vender, alquilar o sublicenciar material de Notiline</T></Typography>
            <Typography component="li" variant="body2"><T>Reproducir, duplicar o copiar material de Notiline</T></Typography>
            <Typography component="li" variant="body2"><T>Redistribuir contenido de Notiline</T></Typography>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            <T>Comentarios de Usuarios</T>
          </Typography>

          <Typography variant="body1" paragraph>
            <T>Partes de esta aplicación ofrecen la oportunidad para que los usuarios publiquen e intercambien opiniones e información. Notiline no filtra, edita, publica ni revisa los Comentarios antes de su presencia en el sitio web.</T>
          </Typography>

          <Typography variant="body1" paragraph>
            <T>Notiline se reserva el derecho de monitorear todos los Comentarios y eliminar cualquier Comentario que pueda considerarse inapropiado, ofensivo o que cause una violación de estos Términos y Condiciones.</T>
          </Typography>

          <Typography variant="body1" paragraph>
            <T>Garantizas y declaras que:</T>
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body2">
              <T>Tienes derecho a publicar los Comentarios en nuestra App y tienes todas las licencias y consentimientos necesarios</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Los Comentarios no invaden ningún derecho de propiedad intelectual</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Los Comentarios no contienen material difamatorio, calumnioso, ofensivo o ilegal</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Los Comentarios no se utilizarán para solicitar o promover actividades comerciales o ilegales</T>
            </Typography>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            <T>Términos de Suscripción y Pagos con PayPal</T>
          </Typography>

          <Typography variant="body1" paragraph>
            <T>Al suscribirte a Notiline a través de PayPal, aceptas los siguientes términos adicionales:</T>
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            <T>1. Suscripción y Pagos</T>
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body2">
              <T>Los pagos se procesarán exclusivamente a través de PayPal</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>La suscripción se renovará automáticamente hasta que se cancele</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Los precios están sujetos a cambios con aviso previo de 30 días</T>
            </Typography>
          </Box>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            <T>2. Proceso de Facturación</T>
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body2">
              <T>El cargo inicial se realizará en el momento de la suscripción</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Los cargos posteriores se realizarán el mismo día de cada período</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>PayPal procesará automáticamente los pagos</T>
            </Typography>
          </Box>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            <T>3. Cancelación de Suscripción</T>
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body2">
              <T>Puedes cancelar en cualquier momento a través de tu cuenta PayPal o Notiline</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>La cancelación será efectiva al final del período actual</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>No se emitirán reembolsos por períodos parciales</T>
            </Typography>
          </Box>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            <T>4. Fallo en el Pago</T>
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body2">
              <T>Si un pago falla, se reintentará durante 3 días</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Después de tres intentos fallidos, la cuenta será suspendida</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Para reactivar, debes actualizar la información de pago</T>
            </Typography>
          </Box>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            <T>5. Cambios en la Suscripción</T>
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body2">
              <T>Puedes cambiar tu plan en cualquier momento</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Las mejoras se aplicarán inmediatamente</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Las degradaciones se aplicarán al siguiente período</T>
            </Typography>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            <T>Responsabilidad del Contenido</T>
          </Typography>

          <Typography variant="body1" paragraph>
            <T>No seremos responsables de ningún contenido que aparezca en tu App. Aceptas protegernos y defendernos contra todas las reclamaciones que surjan. Ningún enlace debe aparecer en cualquier sitio web que pueda interpretarse como difamatorio, obsceno o criminal.</T>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            <T>Reserva de Derechos</T>
          </Typography>

          <Typography variant="body1" paragraph>
            <T>Nos reservamos el derecho de solicitar que elimines todos los enlaces a nuestra App. También nos reservamos el derecho de modificar estos términos en cualquier momento.</T>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            <T>Descargo de Responsabilidad</T>
          </Typography>

          <Typography variant="body1" paragraph>
            <T>En la máxima medida permitida por la ley, excluimos todas las representaciones, garantías y condiciones relacionadas con nuestra App. Nada en este descargo:</T>
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body2">
              <T>Limitará nuestra responsabilidad por muerte o lesiones personales</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Limitará nuestra responsabilidad por fraude</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Limitará responsabilidades no permitidas por ley</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Excluirá responsabilidades que no pueden ser excluidas legalmente</T>
            </Typography>
          </Box>

          <Typography variant="body1" paragraph sx={{ mt: 3, mb: 3 }}>
            <T>Mientras el sitio web y los servicios se proporcionen gratuitamente, no seremos responsables de ninguna pérdida o daño de cualquier naturaleza.</T>
          </Typography>

          <Typography variant="body2" sx={{ mt: 4, fontStyle: "italic", color: "text.secondary" }}>
            {scrolled ? <T>Has leído todos los términos y condiciones. Puedes aceptar ahora.</T> : <T>Por favor, desplázate hasta el final para leer todos los términos.</T>}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          <T>Cancelar</T>
        </Button>
        <Button 
          onClick={onAccept} 
          variant="contained" 
          disabled={!scrolled}
          color="primary"
        >
          <T>Acepto los Términos</T>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Privacy Policy Component
const PrivacyDialog = ({ open, onClose, onAccept }) => {
  const [scrolled, setScrolled] = useState(false);

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 50;
    if (bottom) {
      setScrolled(true);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          height: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6" component="span">
          <T>Política de Privacidad</T>
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers onScroll={handleScroll} sx={{ px: 3 }}>
        <Box sx={{ textAlign: "left" }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            <T>Política de Privacidad de Notiline</T>
          </Typography>

          <Typography variant="body1" paragraph>
            <T>Esta política explica qué información recopila Notiline, para qué la usamos
            y qué opciones tenés sobre tus datos. Notiline es una app operada por Dapur
            Tecnología para publicar y descubrir ofertas, eventos, tiendas, vehículos y
            propiedades/viajes.</T>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            <T>Información que recopilamos</T>
          </Typography>
          <Typography variant="body1" paragraph>
            <T>Recopilamos la información necesaria para que la app funcione:</T>
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body2">
              <T>Datos de cuenta: email, nombre y apellido, nombre de usuario, número de
              documento y teléfono que ingresás al registrarte.</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Datos de perfil: foto de perfil, localidad y, si publicás una tienda,
              autos, propiedades o eventos, la información de esas publicaciones.</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Ubicación: usamos tu ubicación aproximada (por GPS o por IP) para
              mostrarte ofertas, eventos y tiendas cercanas.</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Actividad en la app: ofertas guardadas, tiendas que seguís, mensajes de
              chat, comentarios, preguntas y respuestas, y códigos de cupón escaneados.</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Pagos: si te suscribís o comprás una entrada/cupón, el pago se procesa
              directamente por PayPal. Notiline no almacena números de tarjeta ni datos
              bancarios.</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Notificaciones: un token de dispositivo para enviarte notificaciones push
              sobre ofertas, mensajes y novedades de tu cuenta.</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Registros de acceso: fecha, hora, ubicación aproximada y país desde el que
              iniciás sesión, por seguridad.</T>
            </Typography>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            <T>Cómo usamos tu información</T>
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body2"><T>Mostrarte ofertas, eventos, tiendas, autos y propiedades relevantes según tu ubicación e intereses.</T></Typography>
            <Typography component="li" variant="body2"><T>Procesar suscripciones, compras de entradas y comisiones a través de PayPal.</T></Typography>
            <Typography component="li" variant="body2"><T>Enviarte notificaciones sobre tu actividad (mensajes, respuestas, ofertas guardadas, novedades).</T></Typography>
            <Typography component="li" variant="body2"><T>Detectar accesos sospechosos y proteger tu cuenta, incluyendo la verificación en dos pasos.</T></Typography>
            <Typography component="li" variant="body2"><T>Brindarte soporte cuando nos escribís o usás el asistente de ayuda.</T></Typography>
            <Typography component="li" variant="body2"><T>Mejorar la app y prevenir fraude, spam o abuso.</T></Typography>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            <T>Con quién compartimos información</T>
          </Typography>
          <Typography variant="body1" paragraph>
            <T>No vendemos tu información. La compartimos únicamente cuando es necesario
            para operar Notiline:</T>
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body2">
              <T>Con PayPal, para procesar pagos, suscripciones y reembolsos.</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Con Firebase/Google Cloud, nuestro proveedor de infraestructura, que
              almacena la base de datos, las imágenes y gestiona la autenticación y las
              notificaciones push.</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Con otros usuarios: tu nombre de usuario, foto de perfil y las
              publicaciones que hacés (ofertas, eventos, tienda, autos, propiedades) son
              visibles públicamente dentro de la app, como es esperable en un marketplace.</T>
            </Typography>
            <Typography component="li" variant="body2">
              <T>Cuando la ley lo requiera, o para proteger los derechos, la seguridad o la
              propiedad de Notiline y de sus usuarios.</T>
            </Typography>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            <T>Seguridad de tu cuenta</T>
          </Typography>
          <Typography variant="body1" paragraph>
            <T>Podés activar la verificación en dos pasos desde Ajustes, que agrega un
            código de tu app autenticadora (o un código de respaldo) además de tu
            contraseña para iniciar sesión. Te recomendamos guardar tus códigos de
            respaldo en un lugar seguro: son la única forma de recuperar el acceso si
            perdés tu dispositivo con la app autenticadora.</T>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            <T>Tus derechos sobre tus datos</T>
          </Typography>
          <Typography variant="body1" paragraph>
            <T>Desde la sección de Ajustes y Editar Perfil podés actualizar tus datos de
            cuenta y de perfil en cualquier momento. Si querés eliminar tu cuenta o los
            datos que tenemos sobre vos, o tenés alguna consulta sobre esta política,
            escribinos a</T>{" "}
            <Link href="mailto:support@notiline.com">support@notiline.com</Link>.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            <T>Menores de edad</T>
          </Typography>
          <Typography variant="body1" paragraph>
            <T>Notiline no está dirigido a menores de 13 años y no recopilamos a sabiendas
            información de niños. Si creés que un menor nos proporcionó información
            personal, contactanos para eliminarla.</T>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            <T>Cambios a esta política</T>
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 3 }}>
            <T>Podemos actualizar esta Política de Privacidad para reflejar cambios en la
            app o en la normativa aplicable. Si hacemos cambios importantes, te avisaremos
            dentro de la app.</T>
          </Typography>

          <Typography variant="body2" sx={{ mt: 4, fontStyle: "italic", color: "text.secondary" }}>
            {scrolled ? <T>Has leído toda la política de privacidad. Puedes aceptar ahora.</T> : <T>Por favor, desplázate hasta el final para leer toda la política.</T>}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          <T>Cancelar</T>
        </Button>
        <Button
          onClick={onAccept}
          variant="contained"
          disabled={!scrolled}
          color="primary"
        >
          <T>Acepto la Política de Privacidad</T>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Modo "completar registro": viene del login con Google (ver login.js) con
  // la cuenta ya creada en Auth. Se piden los mismos datos menos email (sale
  // de Google) y contraseña. Al recargar la página auth.currentUser puede
  // tardar en restaurarse, por eso se escucha onAuthStateChanged.
  const googleMode = Boolean(location.state?.google);
  const [googleUser, setGoogleUser] = useState(googleMode ? auth.currentUser : null);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    fullName: "",
    displayName: "",
    nationalId: "",
    phone: "",
    point: "",
  });

  const [selectedCountry, setSelectedCountry] = useState(
    countriesData.find((country) => country.code === "AR") || countriesData[0]
  );

  const [validations, setValidations] = useState({
    email: { error: false, message: "" },
    password: { error: false, message: "" },
    confirmPassword: { error: false, message: "" },
    fullName: { error: false, message: "" },
    displayName: { error: false, message: "" },
    nationalId: { error: false, message: "" },
    phone: { error: false, message: "" },
    point: { error: false, message: "" },
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);

  useEffect(() => {
    if (!googleMode) return undefined;
    return auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate("/login", { replace: true });
        return;
      }
      setGoogleUser(user);
      // Precarga lo que ya haya: el nombre de Google y, si abandonó el
      // formulario antes, lo que hubiera guardado.
      const snap = await db.doc(`users/${user.uid}`).get();
      const data = snap.exists ? snap.data() : {};
      // El teléfono puede estar en el doc privado (ver utils/contactPrivacy.js).
      const contactSnap = await db.doc(`users/${user.uid}/private/contact`).get().catch(() => null);
      const privatePhone = contactSnap?.exists ? contactSnap.data().phone : "";
      setFormData((prev) => ({
        ...prev,
        email: user.email || "",
        fullName: prev.fullName || data.fullname || user.displayName || "",
        displayName: prev.displayName || data.displayName || "",
        phone: prev.phone || privatePhone || data.phone || "",
        point: prev.point || data.point || "",
      }));
    });
  }, [googleMode, navigate]);

  const DEFAULT_PROFILE_IMAGE =
    "https://firebasestorage.googleapis.com/v0/b/notiline-vtoisn.appspot.com/o/user.png?alt=media&token=470b8374-53a9-40c6-8013-68a466566fef";

  const formatPhoneNumber = (value, country) => {
    try {
      const formatter = new AsYouType(country.code);
      let formatted = formatter.input(value);

      if (!formatted.startsWith(country.phoneCode)) {
        formatted = formatted.replace(/^\D+/g, "");
        formatted = `${country.phoneCode} ${formatted}`;
      }

      return formatted;
    } catch (e) {
      return value;
    }
  };

  const handleChange = (field) => (event) => {
    let value = event.target.value;

    if (field === "phone") {
      value = formatPhoneNumber(value, selectedCountry);
    }

    if (field === "nationalId") {
      value = value.toUpperCase();
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setValidations((prev) => ({
      ...prev,
      [field]: { error: false, message: "" },
    }));

    if (error) setError("");
  };

  const [pointOptions, setPointOptions] = useState([]);
  const [pointLoading, setPointLoading] = useState(false);
  const pointSearchTimeout = useRef(null);

  const handlePointInputChange = (event, newInputValue, reason) => {
    setFormData((prev) => ({ ...prev, point: newInputValue }));
    setValidations((prev) => ({
      ...prev,
      point: { error: false, message: "" },
    }));
    if (error) setError("");

    if (reason !== "input") return;

    if (pointSearchTimeout.current) {
      clearTimeout(pointSearchTimeout.current);
    }

    if (newInputValue.trim().length < 3) {
      setPointOptions([]);
      return;
    }

    pointSearchTimeout.current = setTimeout(async () => {
      setPointLoading(true);
      const results = await searchAddressSuggestions(newInputValue);
      setPointOptions(results);
      setPointLoading(false);
    }, 400);
  };

  const handlePointSelect = (event, newValue) => {
    if (newValue && typeof newValue === "object") {
      setFormData((prev) => ({ ...prev, point: newValue.label }));
      setValidations((prev) => ({
        ...prev,
        point: { error: false, message: "" },
      }));
    }
  };

  const handleCountryChange = (event, newValue) => {
    if (newValue) {
      setSelectedCountry(newValue);
      if (formData.phone) {
        const phoneWithoutCode = formData.phone.replace(/^\+\d+\s*/, "");
        const newPhone = formatPhoneNumber(phoneWithoutCode, newValue);
        setFormData((prev) => ({
          ...prev,
          phone: newPhone,
          nationalId: "",
        }));
      }
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || email.trim() === "") {
      return { error: true, message: <T>El email es requerido</T> };
    }
    if (!emailRegex.test(email)) {
      return { error: true, message: <T>Formato de email inválido</T> };
    }
    return { error: false, message: "" };
  };

  const validateFullName = (fullName) => {
    if (!fullName || fullName.trim() === "") {
      return { error: true, message: <T>El nombre completo es requerido</T> };
    }
    if (fullName.trim().length < 5) {
      return {
        error: true,
        message: <T>El nombre completo debe tener al menos 5 caracteres</T>,
      };
    }
    if (!/^[A-Za-záéíóúñÁÉÍÓÚÑ\s]+$/.test(fullName)) {
      return {
        error: true,
        message: <T>El nombre solo puede contener letras y espacios</T>,
      };
    }
    return { error: false, message: "" };
  };

  const validateDisplayName = (displayName) => {
    if (!displayName || displayName.trim() === "") {
      return { error: true, message: <T>El nombre de usuario es requerido</T> };
    }
    if (displayName.trim().length < 3) {
      return {
        error: true,
        message: <T>El nombre de usuario debe tener al menos 3 caracteres</T>,
      };
    }
    if (!/^[A-Za-z0-9_]+$/.test(displayName)) {
      return {
        error: true,
        message: <T>Solo letras, números y guiones bajos</T>,
      };
    }
    return { error: false, message: "" };
  };

  const getPasswordStrength = (password) => {
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password),
    };
    const score = Object.values(checks).filter(Boolean).length;

    const levels = [
      { label: <T>Muy débil</T>, color: "error" },
      { label: <T>Débil</T>, color: "error" },
      { label: <T>Media</T>, color: "warning" },
      { label: <T>Fuerte</T>, color: "info" },
      { label: <T>Muy fuerte</T>, color: "success" },
    ];

    return { checks, score, ...levels[Math.max(0, score - 1)] };
  };

  const isStrongPassword = (password) => {
    const { checks } = getPasswordStrength(password);
    return Object.values(checks).every(Boolean);
  };

  const validatePassword = (password) => {
    if (!password) {
      return { error: true, message: <T>La contraseña es requerida</T> };
    }
    if (!isStrongPassword(password)) {
      return {
        error: true,
        message: (
          <T>
            Debe tener al menos 8 caracteres, una mayúscula, una minúscula, un número y un carácter especial
          </T>
        ),
      };
    }
    return { error: false, message: "" };
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    if (!confirmPassword) {
      return { error: true, message: <T>Debe confirmar la contraseña</T> };
    }
    if (password !== confirmPassword) {
      return { error: true, message: <T>Las contraseñas no coinciden</T> };
    }
    return { error: false, message: "" };
  };

  const validatePhone = (phoneNumber, country) => {
    if (!phoneNumber.trim()) {
      return { error: true, message: <T>El número de teléfono es requerido</T> };
    }

    try {
      const fullNumber = phoneNumber.startsWith("+")
        ? phoneNumber
        : `${country.phoneCode}${phoneNumber.replace(/^\D+/g, "")}`;

      const parsedNumber = parsePhoneNumber(fullNumber, country.code);

      if (!parsedNumber.isValid()) {
        return { error: true, message: <T>Número inválido para {country.name}</T> };
      }

      return { error: false, message: "" };
    } catch (error) {
      return { error: true, message: <T>Formato de número inválido</T> };
    }
  };

  const validateForm = () => {
    const ok = { error: false, message: "" };
    const newValidations = {
      email: googleMode ? ok : validateEmail(formData.email),
      password: googleMode ? ok : validatePassword(formData.password),
      confirmPassword: googleMode
        ? ok
        : validateConfirmPassword(formData.confirmPassword, formData.password),
      fullName: validateFullName(formData.fullName),
      displayName: validateDisplayName(formData.displayName),
      point: validatePoint(formData.point),
      phone: validatePhone(formData.phone, selectedCountry),
    };

    setValidations(newValidations);
    return !Object.values(newValidations).some(
      (validation) => validation.error
    );
  };

  const validatePoint = (point) => {
    if (!point || point.trim() === "") {
      return { error: true, message: <T>La localidad es requerida</T> };
    }
    return { error: false, message: "" };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const fullNumber = formData.phone.startsWith("+")
        ? formData.phone
        : `${selectedCountry.phoneCode}${formData.phone.replace(/^\D+/g, "")}`;

      if (googleMode) {
        if (!googleUser) return;
        await completeGoogleProfile(
          googleUser.uid,
          formData.displayName,
          formData.fullName,
          fullNumber,
          formData.point
        );
      } else {
        await createUser(
          formData.email,
          formData.password,
          formData.displayName,
          formData.fullName,
          fullNumber,
          formData.point
        );
      }

      // La suscripción se pide recién al intentar publicar (ProtectedPublish
      // y PublishSpeedDial), no apenas se crea la cuenta.
      navigate("/home");
    } catch (error) {
      setError(
        error.code === "auth/email-already-in-use"
          ? <T>Este email ya está registrado</T>
          : error.message
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenTerms = (e) => {
    e.preventDefault();
    setTermsDialogOpen(true);
  };

  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    setTermsDialogOpen(false);
  };

  const handleCloseTerms = () => {
    setTermsDialogOpen(false);
  };

  const handleOpenPrivacy = (e) => {
    e.preventDefault();
    setPrivacyDialogOpen(true);
  };

  const handleAcceptPrivacy = () => {
    setPrivacyAccepted(true);
    setPrivacyDialogOpen(false);
  };

  const handleClosePrivacy = () => {
    setPrivacyDialogOpen(false);
  };

  return (
    <AuthLayout>
    <Container maxWidth="xs">
      <Card elevation={3} sx={{ mt: 4, mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Stack spacing={2} alignItems="center">
            <CardMedia
              component="img"
              image={brandColors.mode === 'dark' ? logo13 : logo15}
              title="Notiline"
              sx={{
                width: 180,
                height: "auto",
                mx: "auto",
                mt: 4,
                mb: 2,
              }}
            />

            <Typography variant="h5" component="h1" fontWeight="300">
              {googleMode ? <T>Completá tu registro</T> : <T>Registrarse</T>}
            </Typography>

            {googleMode && (
              <Typography variant="body2" textAlign="center" sx={{ color: brandColors.textSecondary }}>
                <T>Ya iniciaste sesión con Google. Faltan algunos datos para crear tu cuenta.</T>
              </Typography>
            )}

            {error && (
              <Alert severity="error" sx={{ width: "100%", borderRadius: 1 }}>
                {error}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ width: "100%" }}
            >
              <TextField
                fullWidth
                margin="normal"
                id="email"
                label={<T>Email</T>}
                type="email"
                value={formData.email}
                onChange={handleChange("email")}
                error={validations.email.error}
                helperText={validations.email.message}
                disabled={isLoading || googleMode}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
                autoComplete="email"
              />

              <TextField
                fullWidth
                margin="normal"
                id="displayName"
                label={<T>Usuario</T>}
                value={formData.displayName}
                onChange={handleChange("displayName")}
                error={validations.displayName.error}
                helperText={validations.displayName.message}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                margin="normal"
                id="fullName"
                label={<T>Nombre y Apellido</T>}
                value={formData.fullName}
                onChange={handleChange("fullName")}
                error={validations.fullName.error}
                helperText={validations.fullName.message}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
                autoComplete="name"
              />

              <Autocomplete
                options={countriesData}
                value={selectedCountry}
                onChange={handleCountryChange}
                getOptionLabel={(option) =>
                  `${option.flag} ${option.name} (${option.phoneCode})`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={<T>País</T>}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.code === value.code
                }
                disabled={isLoading}
              />

              <TextField
                fullWidth
                margin="normal"
                id="phone"
                label={<T>Teléfono</T>}
                value={formData.phone}
                onChange={handleChange("phone")}
                error={validations.phone.error}
                helperText={
                  validations.phone.message ||
                  <><T>Formato para</T> {selectedCountry.name}: {selectedCountry.phoneCode}</>
                }
                disabled={isLoading}
                placeholder={`${selectedCountry.phoneCode} XXXXXXXXX`}
              />

              <Autocomplete
                freeSolo
                fullWidth
                filterOptions={(options) => options}
                options={pointOptions}
                getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
                isOptionEqualToValue={(option, value) => option.label === value.label}
                getOptionKey={(option) =>
                  typeof option === "string" ? option : `${option.label}-${option.latitude}-${option.longitude}`
                }
                loading={pointLoading}
                inputValue={formData.point}
                onInputChange={handlePointInputChange}
                onChange={handlePointSelect}
                disabled={isLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="normal"
                    id="point"
                    label={<T>Localidad</T>}
                    error={validations.point.error}
                    helperText={validations.point.message}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <LocationIcon />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {pointLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {!googleMode && (<>
              <TextField
                fullWidth
                margin="normal"
                id="password"
                label={<T>Contraseña</T>}
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange("password")}
                error={validations.password.error}
                helperText={validations.password.message}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={isLoading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                autoComplete="new-password"
              />

              {formData.password && (
                <Box sx={{ px: 0.5, mt: -1, mb: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(getPasswordStrength(formData.password).score / 5) * 100}
                    color={getPasswordStrength(formData.password).color}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                  <Typography
                    variant="caption"
                    color={`${getPasswordStrength(formData.password).color}.main`}
                  >
                    {getPasswordStrength(formData.password).label}
                  </Typography>
                </Box>
              )}

              <TextField
                fullWidth
                margin="normal"
                id="confirmPassword"
                label={<T>Confirmar Contraseña</T>}
                type={showPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleChange("confirmPassword")}
                error={validations.confirmPassword.error}
                helperText={validations.confirmPassword.message}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={isLoading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                autoComplete="new-password"
              />
              </>)}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    name="termsAccepted"
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    <T>Acepto los</T>{" "}
                    <Link
                      href="#"
                      onClick={handleOpenTerms}
                      sx={{ cursor: "pointer", color: brandColors.accent }}
                    >
                      <T>Términos y Condiciones</T>
                    </Link>
                  </Typography>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    name="privacyAccepted"
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    <T>Acepto la</T>{" "}
                    <Link
                      href="#"
                      onClick={handleOpenPrivacy}
                      sx={{ cursor: "pointer", color: brandColors.accent }}
                    >
                      <T>Política de Privacidad</T>
                    </Link>
                  </Typography>
                }
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading || !termsAccepted || !privacyAccepted || (googleMode && !googleUser)}
                sx={{
                  mt: 3,
                  mb: 2,
                  height: 42,
                  textTransform: "none",
                  fontSize: "1rem",
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : googleMode ? (
                  <T>Continuar</T>
                ) : (
                  <T>Registrarse</T>
                )}
              </Button>

              {!googleMode && (
              <Box textAlign="center" sx={{ mt: 2, mb: 2 }}>
                <T>¿Ya tienes cuenta?</T>{" "}
                <Link
                  href="/login"
                  sx={{
                    color: brandColors.accent,
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  <T>Iniciar sesión</T>
                </Link>
              </Box>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Terms and Conditions Dialog */}
      <TermsDialog
        open={termsDialogOpen}
        onClose={handleCloseTerms}
        onAccept={handleAcceptTerms}
      />

      {/* Privacy Policy Dialog */}
      <PrivacyDialog
        open={privacyDialogOpen}
        onClose={handleClosePrivacy}
        onAccept={handleAcceptPrivacy}
      />
    </Container>
    </AuthLayout>
  );
};

export default Register;